import { Component, Input, OnInit } from '@angular/core';

import {
  IntersectionData,
  QueueCharacteristics,
  SiterecordCharacteristics,
  SiterecordCharacteristicsExtended,
  SiterecordIntersections
} from 'app/models';
import {
  CarriagewayType,
  ComputationMethodType,
  EquipmentType,
  LaneType,
  MeasurementCategoryType,
  MeasurementValueTypePoint,
  MeasurementValueTypePointVri,
  QueueDetectionMethodType,
  QueueType,
  SideType,
  SpecificLaneCompositionType,
  TrafficSortingCharacteristicsType
} from 'app/models/enums';
import { CbmInputStyle } from 'app/shared/input/cbm-input-style';
import { enumValues } from 'app/shared/util/enumValues';

@Component({
  selector: 'point-details-intersections',
  templateUrl: './point-details-intersections.component.html',
  styleUrls: ['./point-details.component.scss']
})
export class PointDetailsIntersectionsComponent implements OnInit {
  @Input() id: string;
  @Input() readonly: boolean;
  @Input() inputStyle: CbmInputStyle;

  @Input() set siterecordIntersections(siterecordIntersections: SiterecordIntersections[]) {
    this._siterecordIntersections = siterecordIntersections;
    this.setVRIIntersectionInformation();
  }

  get siterecordIntersections() {
    return this._siterecordIntersections;
  }

  @Input() set siterecordCharacteristics(siterecordCharacteristics: SiterecordCharacteristics[]) {
    this._siterecordCharacteristics = siterecordCharacteristics;
    this.setVRICharacteristicsInformation();
  }

  get siterecordCharacteristics() {
    return this._siterecordCharacteristics;
  }

  private _siterecordIntersections: SiterecordIntersections[];
  private _siterecordCharacteristics: SiterecordCharacteristics[];

  public title: string;
  public isEquipmentTypeVRI: boolean;

  public siterecordIntersectionsTrafficlightSignalGroups: SiterecordIntersections[];
  public siterecordIntersectionsTrafficSortingCharacteristics: SiterecordIntersections[];
  public siterecordIntersectionsMaxLineupLengthAvailable: SiterecordIntersections[];
  public queueCharacteristicsQueueTypes: [QueueCharacteristics[]];
  public queueCharacteristicsQueueDetectionMethods: [QueueCharacteristics[]];
  public intersectionDataSpecificLaneCompositions: [IntersectionData[]];
  public intersectionDataTrafficSortingCharacteristics: [IntersectionData[]];
  public intersectionDataTrafficlightSignalgroup: IntersectionData[];

  public siterecordIntersectionsTrafficlightSignalGroupsValues: string[];
  public siterecordIntersectionsTrafficSortingCharacteristicsValues: string[];
  public queueCharacteristicsQueueTypesValues: [string[]];
  public queueCharacteristicsQueueDetectionMethodsValues: [string[]];
  public intersectionDataSpecificLaneCompositionsValues: [string[]];
  public intersectionDataTrafficSortingCharacteristicsValues: [string[]];
  public intersectionDataTrafficlightSignalgroupValues: [string[]];

  public computationMethods: string[];
  public equipmentTypes: string[];
  public sideTypes: string[];
  public carriagewayTypes: string[];
  public measurementValueTypes: string[];
  public measurementValueTypesVri: string[];
  public measurementCategoryTypes: string[];
  public specificLanes: string[];
  public queueTypes: string[];
  public queueDetectionMethodTypes: string[];
  public specificLaneCompositionTypes: string[];
  public trafficSortingCharacteristics: string[];

  constructor() {}

  ngOnInit(): void {
    this.initialiseEnums();
  }

  private initialiseEnums(): void {
    this.computationMethods = enumValues(ComputationMethodType);
    this.equipmentTypes = enumValues(EquipmentType);
    this.carriagewayTypes = enumValues(CarriagewayType);
    this.sideTypes = enumValues(SideType);

    this.specificLanes = enumValues(LaneType);
    this.measurementValueTypes = enumValues(MeasurementValueTypePoint);
    this.measurementValueTypesVri = enumValues(MeasurementValueTypePointVri);
    this.measurementCategoryTypes = enumValues(MeasurementCategoryType);

    this.queueTypes = enumValues(QueueType);
    this.queueDetectionMethodTypes = enumValues(QueueDetectionMethodType);

    this.specificLaneCompositionTypes = enumValues(SpecificLaneCompositionType);
    this.trafficSortingCharacteristics = enumValues(TrafficSortingCharacteristicsType);
  }
  private setVRIIntersectionInformation() {
    this.siterecordIntersectionsMaxLineupLengthAvailable = [];
    this.siterecordIntersectionsTrafficlightSignalGroups = [];
    this.siterecordIntersectionsTrafficSortingCharacteristics = [];

    for (const siterecordIntersection of this.siterecordIntersections) {
      if (siterecordIntersection.maxLineupLengthAvailable) {
        this.siterecordIntersectionsMaxLineupLengthAvailable.push(siterecordIntersection);
      } else if (siterecordIntersection.trafficlightSignalGroup) {
        this.siterecordIntersectionsTrafficlightSignalGroups.push(siterecordIntersection);
      } else if (siterecordIntersection.trafficSortingCharacteristics) {
        this.siterecordIntersectionsTrafficSortingCharacteristics.push(siterecordIntersection);
      }
    }
    if (this.siterecordIntersectionsMaxLineupLengthAvailable.length === 0) {
      const intersections = new SiterecordIntersections();
      intersections.intersectionsOrder = this.siterecordIntersections.length + 1;
      this.siterecordIntersectionsMaxLineupLengthAvailable.push(intersections);
      this.siterecordIntersections.push(intersections);
    }
  }

  private setVRICharacteristicsInformation() {
    this.intersectionDataTrafficlightSignalgroup = [];
    this.intersectionDataSpecificLaneCompositions = [[]];
    this.intersectionDataTrafficSortingCharacteristics = [[]];
    this.queueCharacteristicsQueueTypes = [[]];
    this.queueCharacteristicsQueueDetectionMethods = [[]];

    for (let i = 0; i < this.siterecordCharacteristics.length; i++) {
      const siterecordCharacteristicsExtended = this.siterecordCharacteristics[i].siterecordCharacteristicsExtended;
      for (const intersectionData of siterecordCharacteristicsExtended.intersectionData) {
        if (intersectionData.trafficlightSignalGroup) {
          this.intersectionDataTrafficlightSignalgroup[i] = intersectionData;
        } else if (intersectionData.specificLaneComposition) {
          if (!this.intersectionDataSpecificLaneCompositions[i]) {
            this.intersectionDataSpecificLaneCompositions[i] = [];
          }
          this.intersectionDataSpecificLaneCompositions[i].push(intersectionData);
        } else if (intersectionData.trafficSortingCharacteristics) {
          if (!this.intersectionDataTrafficSortingCharacteristics[i]) {
            this.intersectionDataTrafficSortingCharacteristics[i] = [];
          }
          this.intersectionDataTrafficSortingCharacteristics[i].push(intersectionData);
        }
      }

      for (const queueCharacteristics of siterecordCharacteristicsExtended.queueCharacteristics) {
        if (queueCharacteristics.queueType) {
          if (!this.queueCharacteristicsQueueTypes[i]) {
            this.queueCharacteristicsQueueTypes[i] = [];
          }
          this.queueCharacteristicsQueueTypes[i].push(queueCharacteristics);
        } else if (queueCharacteristics.queueDetectionMethod) {
          if (!this.queueCharacteristicsQueueDetectionMethods[i]) {
            this.queueCharacteristicsQueueDetectionMethods[i] = [];
          }
          this.queueCharacteristicsQueueDetectionMethods[i].push(queueCharacteristics);
        }
      }
    }
  }

  public setIntersectionDataTrafficLight(index: number) {
    this.siterecordCharacteristics[
      index
    ].siterecordCharacteristicsExtended.intersectionData[0].trafficlightSignalGroup =
      this.siterecordIntersectionsTrafficlightSignalGroups[index].trafficlightSignalGroup;
    this.intersectionDataTrafficlightSignalgroup[index].trafficlightSignalGroup =
      this.siterecordIntersectionsTrafficlightSignalGroups[index].trafficlightSignalGroup;
  }

  public hasMaxSpecificLaneCompositionTypes(): boolean {
    return this.intersectionDataSpecificLaneCompositions.length === this.specificLaneCompositionTypes.length;
  }

  public hasMaxQueueDetectionMethods(): boolean {
    return this.queueCharacteristicsQueueDetectionMethods.length === this.queueDetectionMethodTypes.length;
  }

  public hasMaxQueueTypes(): boolean {
    return this.queueCharacteristicsQueueTypes.length === this.queueTypes.length;
  }

  public measurementValueTypeChanged(index: number, measurementValueType: string) {
    if (measurementValueType === 'trafficStatusInformation') {
      console.log(this.siterecordCharacteristics[index].siterecordCharacteristicsExtended);
      if (
        !this.siterecordCharacteristics[index].siterecordCharacteristicsExtended.queueCharacteristics ||
        this.siterecordCharacteristics[index].siterecordCharacteristicsExtended.queueCharacteristics.length === 0
      ) {
        this.addQueueCharacteristics(index, this.siterecordCharacteristics[index], 'queueCharacteristics');
      }
    } else if (this.siterecordCharacteristics[index].siterecordCharacteristicsExtended.queueCharacteristics) {
      this.removeQueueCharacteristics(index, this.siterecordCharacteristics[index], 'queueCharacteristics');
    }
  }

  public addSiterecordIntersections(type: string): void {
    const intersections = new SiterecordIntersections();
    if (type === 'trafficlightSignalGroup') {
      intersections.intersectionsOrder = this.siterecordIntersections.length + 1;
      this.siterecordIntersectionsTrafficlightSignalGroups.push(intersections);

      const characteristics = new SiterecordCharacteristics();
      const characteristicsExtended = new SiterecordCharacteristicsExtended();
      const intersectionData = new IntersectionData();

      intersectionData.intersectionDataOrder = 1;
      characteristicsExtended.intersectionData = [intersectionData];
      characteristics.siterecordCharacteristicsExtended = characteristicsExtended;
      characteristics.characteristicsOrder = this.siterecordCharacteristics.length + 1;

      this.siterecordCharacteristics.push(characteristics);

      const index = this.siterecordIntersectionsTrafficlightSignalGroups.length - 1;
      this.addIntersectionData(index, this.siterecordCharacteristics[index], 'specificLaneComposition');
      this.addIntersectionData(index, this.siterecordCharacteristics[index], 'trafficSortingCharacteristics');

      this.intersectionDataTrafficlightSignalgroup[index] = intersectionData;
    } else if (type === 'trafficSortingCharacteristics') {
      intersections.intersectionsOrder = this.siterecordIntersections.length + 1;
      this.siterecordIntersectionsTrafficSortingCharacteristics.push(intersections);
    }
    this.siterecordIntersections.push(intersections);
  }

  public removeSiterecordIntersections(type: string): void {
    let intersections;
    if (type === 'trafficlightSignalGroup') {
      let index = this.siterecordIntersectionsTrafficlightSignalGroups.length - 1;
      const trafficlightSignalGroup =
        this.siterecordIntersectionsTrafficlightSignalGroups[index].trafficlightSignalGroup;
      for (const siterecordIntersection of this.siterecordIntersections) {
        if (
          siterecordIntersection.trafficlightSignalGroup &&
          siterecordIntersection.trafficlightSignalGroup === trafficlightSignalGroup
        ) {
          this.siterecordIntersections.splice(this.siterecordIntersections.indexOf(siterecordIntersection), 1);
        }
      }
      intersections = this.siterecordIntersectionsTrafficlightSignalGroups.pop();

      index = this.siterecordIntersectionsTrafficlightSignalGroups.length;
      this.removeIntersectionData(index, this.siterecordCharacteristics[index], 'intersectionData');
      // this.removeIntersectionData(index, this.siterecordCharacteristics[index], 'specificLaneComposition');
      // this.removeIntersectionData(index, this.siterecordCharacteristics[index], 'trafficSortingCharacteristics');
    } else if (type === 'trafficSortingCharacteristics') {
      intersections = this.siterecordIntersectionsTrafficSortingCharacteristics.pop();
    }

    if (intersections) {
      const siterecordIntersections = this.siterecordIntersections;
      siterecordIntersections.splice(siterecordIntersections.indexOf(intersections), 1);

      if (siterecordIntersections.length === 0) {
        this.siterecordIntersections = null;
      }
    }
  }

  public addIntersectionData(index: number, siterecordCharacteristics: SiterecordCharacteristics, type: string): void {
    const intersectionData = new IntersectionData();
    if (type === 'specificLaneComposition') {
      if (!this.intersectionDataSpecificLaneCompositions[index]) {
        this.intersectionDataSpecificLaneCompositions[index] = [];
      }
      this.intersectionDataSpecificLaneCompositions[index].push(intersectionData);
    } else if (type === 'trafficSortingCharacteristics') {
      if (!this.intersectionDataTrafficSortingCharacteristics[index]) {
        this.intersectionDataTrafficSortingCharacteristics[index] = [];
      }
      this.intersectionDataTrafficSortingCharacteristics[index].push(intersectionData);
    }

    if (!siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData) {
      siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData = [intersectionData];
    }
    intersectionData.intersectionDataOrder =
      siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData.length + 1;
    siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData.push(intersectionData);
  }

  public removeIntersectionData(
    index: number,
    siterecordCharacteristics: SiterecordCharacteristics,
    type: string
  ): void {
    let intersectionData;
    if (type === 'intersectionData') {
      siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData = null;
      this.intersectionDataSpecificLaneCompositions[index] = [];
      this.intersectionDataTrafficSortingCharacteristics[index] = [];
      this.siterecordCharacteristics.pop();
    } else if (type === 'specificLaneComposition') {
      intersectionData = this.intersectionDataSpecificLaneCompositions[index].pop();
    } else if (type === 'trafficSortingCharacteristics') {
      intersectionData = this.intersectionDataTrafficSortingCharacteristics[index].pop();
    }

    if (intersectionData) {
      const data = siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData;
      data.splice(data.indexOf(intersectionData), 1);
    }
  }

  public addQueueCharacteristics(
    index: number,
    siterecordCharacteristics: SiterecordCharacteristics,
    type: string
  ): void {
    const queueCharacteristics = new QueueCharacteristics();
    if (type === 'queueCharacteristics') {
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics = [];

      const queueTypes = new QueueCharacteristics();
      const queueDetectionMethodTypes = new QueueCharacteristics();

      if (!this.queueCharacteristicsQueueTypes[index]) {
        this.queueCharacteristicsQueueTypes[index] = [];
      }
      queueTypes.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueTypes[index].push(queueTypes);

      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueTypes);

      if (!this.queueCharacteristicsQueueDetectionMethods[index]) {
        this.queueCharacteristicsQueueDetectionMethods[index] = [];
      }
      queueDetectionMethodTypes.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueDetectionMethods[index].push(queueDetectionMethodTypes);

      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueDetectionMethodTypes);
    } else if (type === 'queueType') {
      if (!this.queueCharacteristicsQueueTypes[index]) {
        this.queueCharacteristicsQueueTypes[index] = [];
      }
      queueCharacteristics.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueTypes[index].push(queueCharacteristics);
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueCharacteristics);
    } else if (type === 'queueDetectionMethodType') {
      if (!this.queueCharacteristicsQueueDetectionMethods[index]) {
        this.queueCharacteristicsQueueDetectionMethods[index] = [];
      }
      queueCharacteristics.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueDetectionMethods[index].push(queueCharacteristics);
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueCharacteristics);
    }
  }

  public removeQueueCharacteristics(
    index: number,
    siterecordCharacteristics: SiterecordCharacteristics,
    type: string
  ): void {
    let queueCharacteristics;
    if (type === 'queueCharacteristics') {
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics = null;

      this.queueCharacteristicsQueueTypes[index] = [];
      this.queueCharacteristicsQueueDetectionMethods[index] = [];

      // siterecordCharacteristics.siterecordCharacteristicsExtended.timeToForecastPeriodStart = null;
    } else if (type === 'queueType') {
      queueCharacteristics = this.queueCharacteristicsQueueTypes[index].pop();
    } else if (type === 'queueDetectionMethodType') {
      queueCharacteristics = this.queueCharacteristicsQueueDetectionMethods[index].pop();
    }

    if (queueCharacteristics) {
      const queue = siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics;
      queue.splice(queue.indexOf(queueCharacteristics), 1);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/point-details/general/point-details-intersections.component.ts